import { Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { ContractOverviewDto, ContractReportQueryDto, SupplierValueDto } from "../../autogen/bff-api";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { registerLink } from "./contractFigures";
import { OverviewBar } from "./OverviewBar";

export const ValuePerSupplier = ({ overview, base }: { overview: ContractOverviewDto; base: ContractReportQueryDto }) => {
  const { t } = useTranslation();
  const { top, others } = overview.valuePerSupplier;
  const frameworkScope = !!base.activeFrameworkAgreement;
  const rows = [...top, ...(others ? [others] : [])];
  const total = rows.reduce((sum, row) => sum + row.annualValue, 0);
  const largest = Math.max(1, ...rows.map((row) => row.annualValue));
  const value = (row: SupplierValueDto) =>
    displaySum({ value: Math.round(row.annualValue), currency: overview.annualValueCurrency });
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("Value per supplier")}
      </Text>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {frameworkScope
            ? t("No supplier in an active framework agreement has an annual value yet.")
            : t("No active contract has an annual value yet.")}
        </Text>
      ) : (
        <Stack spacing="1">
          {rows.map((row, index) => (
            <OverviewBar
              key={row.counterpartyId ?? "others"}
              label={row === others ? String(t("Others")) : row.name || String(t("Unknown supplier"))}
              width={(row.annualValue / largest) * 100}
              shown={value(row)}
              details={[
                t("Annual value: {{value}}", { value: value(row) }),
                frameworkScope
                  ? t("{{count}} active framework agreements", { count: row.contracts })
                  : t("{{count}} active contracts", { count: row.contracts }),
                t("{{share}} % of the total", {
                  share: formatNumber(Math.round((total > 0 ? row.annualValue / total : 0) * 1000) / 10),
                }),
              ]}
              link={row.filter ? registerLink(base, { filter: row.filter }) : null}
              testId={row === others ? "supplier-others" : `supplier-${index}`}
            />
          ))}
        </Stack>
      )}
      {overview.activeWithoutAnnualValue + overview.activeInOtherCurrency > 0 && (
        <Text fontSize="sm" color="smMuted">
          {frameworkScope
            ? t(
                "{{count}} suppliers in active framework agreements without an annual value in {{currency}} are not in the bars.",
                {
                  count: overview.activeWithoutAnnualValue + overview.activeInOtherCurrency,
                  currency: overview.annualValueCurrency,
                },
              )
            : t("{{count}} active contracts without an annual value in {{currency}} are not in the bars.", {
                count: overview.activeWithoutAnnualValue + overview.activeInOtherCurrency,
                currency: overview.annualValueCurrency,
              })}
        </Text>
      )}
    </Stack>
  );
};
